/**
 * TopBarDraftsDropdown — Day 76 P5.1 Phase 8E.
 *
 * Discoverability surface for buying session drafts per Sol Phase 0 lock
 * 2026-05-17 option (b) — TopBar dropdown (sibling shape to TopBar
 * notifications panel). Closes Day 55+ BACKLOG follow-up "Drafts tray on
 * Dashboard (or TopBar drafts dropdown)."
 *
 * Per-row actions: rename (inline edit), delete (with ConfirmDialog),
 * restore (navigates to /purchasing/buying-session?session={uuid}).
 *
 * Drafts list refetches on dropdown open + after rename/delete actions.
 * Empty state mirrors notifications panel "No drafts" pattern.
 */

import { useState, useRef, useEffect, useCallback, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Pencil, Trash2, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useBuyingSession, type BuyingSessionRecord } from '../../hooks/useBuyingSession';
import ConfirmDialog from '../ui/ConfirmDialog';

// Local relativeTime mirrors TopBar.tsx pattern (see Phase 6 fmt$ surface
// finding — 15+ inline definitions; consolidation is Day 77+ follow-up).
function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

export default function TopBarDraftsDropdown(): JSX.Element {
  const navigate = useNavigate();
  const { listMyDrafts } = useBuyingSession(null);
  const [open, setOpen] = useState<boolean>(false);
  const [drafts, setDrafts] = useState<BuyingSessionRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState<string>('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const renameInputRef = useRef<HTMLInputElement | null>(null);

  // Focus the rename input when renaming starts (replaces autoFocus prop per
  // jsx-a11y/no-autofocus — same pattern as the rest of the app: ref +
  // useEffect, e.g. saveAsDraftNameRef in BuyingSession.tsx).
  useEffect(() => {
    if (!renamingId) return;
    renameInputRef.current?.focus();
    renameInputRef.current?.select();
  }, [renamingId]);

  // Fetch on open + after mutations
  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const data = await listMyDrafts();
      setDrafts(data);
    } finally {
      setLoading(false);
    }
  }, [listMyDrafts]);

  useEffect(() => {
    if (!open) return;
    void refetch();
  }, [open, refetch]);

  // Close on click outside / Escape — mirrors TopBar.tsx notifications panel
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent): void {
      const target = e.target as Node | null;
      if (panelRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      setOpen(false);
    }
    function handleEscape(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  async function handleRename(id: string): Promise<void> {
    const trimmed = renameDraft.trim();
    setRenamingId(null);
    if (!trimmed) return;
    const { error } = await supabase.from('buying_sessions').update({ name: trimmed }).eq('id', id);
    if (!error) {
      setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, name: trimmed } : d)));
    }
  }

  async function handleDelete(id: string): Promise<void> {
    const { error } = await supabase.from('buying_sessions').delete().eq('id', id);
    setDeletingId(null);
    if (!error) {
      setDrafts((prev) => prev.filter((d) => d.id !== id));
    }
  }

  function handleRestore(id: string): void {
    setOpen(false);
    navigate(`/purchasing/buying-session?session=${id}`);
  }

  const deleteTargetName = deletingId
    ? drafts.find((d) => d.id === deletingId)?.name || 'Untitled draft'
    : '';

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen((prev) => !prev)}
        className="p-1.5 rounded text-ink-muted hover:text-ink-primary hover:bg-white/10 transition-colors cursor-pointer"
        title="Buying session drafts"
        aria-label="Buying session drafts"
      >
        <FileText size={14} />
      </button>

      {open && (
        <div ref={panelRef}>
          <div
            className="fixed top-[52px] right-0 w-80 h-[calc(100vh-52px-36px)] bg-surface-1 border-l border-border shadow-lg flex flex-col"
            style={{ zIndex: 'var(--z-dropdown, 100)' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
              <span className="text-sm font-semibold text-ink-bright">Buying Drafts</span>
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded text-ink-muted hover:text-ink-primary hover:bg-surface-3 transition-colors cursor-pointer"
                aria-label="Close drafts"
              >
                <X size={14} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto pr-2.5">
              {loading ? (
                <div className="flex items-center justify-center h-32 text-xs text-ink-muted">
                  Loading…
                </div>
              ) : drafts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center px-4">
                  <FileText size={48} className="text-ink-muted mb-3" />
                  <p className="text-sm text-ink-muted">No drafts</p>
                </div>
              ) : (
                drafts.map((d) => (
                  <div
                    key={d.id}
                    className="flex items-start gap-2 px-4 py-3 border-b border-border-subtle hover:bg-surface-2 transition-colors group"
                  >
                    <div className="flex-1 min-w-0">
                      {renamingId === d.id ? (
                        <input
                          ref={renameInputRef}
                          type="text"
                          value={renameDraft}
                          onChange={(e) => setRenameDraft(e.target.value)}
                          onBlur={() => void handleRename(d.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') void handleRename(d.id);
                            if (e.key === 'Escape') setRenamingId(null);
                          }}
                          className="w-full px-1 py-0.5 text-xs bg-surface-2 border border-border rounded text-ink-primary focus:outline-none focus:border-accent"
                          aria-label="Rename draft"
                        />
                      ) : (
                        <button
                          onClick={() => handleRestore(d.id)}
                          className="block w-full text-left text-xs font-medium text-ink-primary hover:text-accent truncate cursor-pointer"
                        >
                          {d.name || 'Untitled draft'}
                        </button>
                      )}
                      <p className="text-2xs text-ink-muted mt-0.5">{relativeTime(d.started_at)}</p>
                    </div>
                    {renamingId !== d.id && (
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <button
                          onClick={() => {
                            setRenameDraft(d.name || '');
                            setRenamingId(d.id);
                          }}
                          className="p-1 rounded text-ink-muted hover:text-ink-primary hover:bg-surface-3 cursor-pointer"
                          aria-label={`Rename ${d.name || 'draft'}`}
                          title="Rename"
                        >
                          <Pencil size={12} />
                        </button>
                        <button
                          onClick={() => setDeletingId(d.id)}
                          className="p-1 rounded text-ink-muted hover:text-danger hover:bg-surface-3 cursor-pointer"
                          aria-label={`Delete ${d.name || 'draft'}`}
                          title="Delete"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deletingId !== null}
        title="Delete draft"
        message={`Delete draft "${deleteTargetName}"? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={() => {
          if (deletingId) void handleDelete(deletingId);
        }}
        onCancel={() => setDeletingId(null)}
      />
    </>
  );
}
